import { Ionicons } from "@expo/vector-icons";
import type { Session } from "@supabase/supabase-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Animated, Easing, Image, Modal, PanResponder, Platform, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from "react-native";
import { agents } from "./data";
import { backendAgentId } from "./supabase";
import { BusinessChange, deleteQna, enablePushNotifications, HistoryRow, listQna, loadBusinessChanges, loadSavedReports, loadSystemHealth, markBusinessChangesSeen, recordUsage, saveNotificationPreferences, updateProfile } from "./productApi";

const C = {
  ink: "#211D19",
  muted: "#6F665D",
  paper: "#F7F3EC",
  card: "#FFFCF7",
  line: "#DED3C5",
  gold: "#B88B53",
  goldSoft: "#EFE0C9",
  bad: "#B84D43",
  white: "#FFF",
};
const backendAgents = agents.map((agent) => ({
  ...agent,
  backendId: backendAgentId[agent.id] ?? agent.id,
}));
const agentName = (id: string) => backendAgents.find((agent) => agent.backendId === id)?.name ?? id.replaceAll("-", " ");

function Header({ title, close }: { title: string; close: () => void }) {
  return (
    <View style={s.header}>
      <Text style={s.title}>{title}</Text>
      <Pressable accessibilityRole="button" accessibilityLabel={`Close ${title}`} onPress={close} style={s.close}>
        <Ionicons name="close" size={22} color={C.ink} />
      </Pressable>
    </View>
  );
}

export function PinGate({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifyPhase, setVerifyPhase] = useState<"entry" | "verifying" | "collapsing">("entry");
  const slotMotion = useRef([0, 1, 2, 3].map(() => new Animated.Value(0))).current;
  const cardMotion = useRef(new Animated.Value(0)).current;
  const formationMotion = useRef(new Animated.Value(0)).current;
  const orbitMotion = useRef(new Animated.Value(0)).current;
  const collapseMotion = useRef(new Animated.Value(0)).current;
  const successMotion = useRef(new Animated.Value(0)).current;
  const reducedMotion = Platform.OS === "web" && typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animateSlot = (index: number) => {
    const motion = slotMotion[index];
    motion.setValue(0);
    Animated.spring(motion, {
      toValue: 1,
      speed: 18,
      bounciness: 9,
      useNativeDriver: true,
    }).start();
  };
  const shakeCard = () => {
    cardMotion.setValue(0);
    Animated.sequence(
      [-9, 8, -6, 4, 0].map((toValue) =>
        Animated.timing(cardMotion, {
          toValue,
          duration: 55,
          useNativeDriver: true,
        }),
      ),
    ).start();
  };
  const submit = async (next: string) => {
    if (next.length !== 4 || busy) return;
    cardMotion.stopAnimation();
    cardMotion.setValue(0);
    setBusy(true);
    setError("");
    collapseMotion.setValue(0);
    formationMotion.setValue(reducedMotion ? 1 : 0);
    orbitMotion.setValue(0);
    setVerifyPhase("verifying");
    const motionDone = reducedMotion
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          Animated.sequence([
            Animated.timing(formationMotion, {
              toValue: 1,
              duration: 240,
              easing: Easing.out(Easing.cubic),
              useNativeDriver: true,
            }),
            Animated.timing(orbitMotion, {
              toValue: 1,
              duration: 1350,
              easing: Easing.inOut(Easing.sin),
              useNativeDriver: true,
            }),
            Animated.delay(140),
          ]).start(() => resolve());
        });
    try {
      const { signInWithPin } = await import("./productApi");
      const { supabase } = await import("./supabase");
      const tokens = await signInWithPin(next);
      const { data, error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (sessionError || !data.session) throw sessionError ?? new Error("Could not start the account session.");
      await motionDone;
      formationMotion.stopAnimation();
      formationMotion.setValue(1);
      if (reducedMotion) {
        setVerified(true);
        setTimeout(() => onSignedIn(data.session!), 450);
      } else {
        setVerifyPhase("collapsing");
        Animated.timing(collapseMotion, {
          toValue: 1,
          duration: 420,
          useNativeDriver: true,
        }).start(() => {
          setVerified(true);
          successMotion.setValue(0);
          Animated.spring(successMotion, {
            toValue: 1,
            speed: 12,
            bounciness: 7,
            useNativeDriver: true,
          }).start();
          setTimeout(() => onSignedIn(data.session!), 900);
        });
      }
    } catch (cause) {
      formationMotion.stopAnimation();
      orbitMotion.stopAnimation();
      formationMotion.setValue(0);
      orbitMotion.setValue(0);
      collapseMotion.setValue(0);
      setVerifyPhase("entry");
      setError(cause instanceof Error ? cause.message : "PIN is incorrect.");
      setPin("");
      slotMotion.forEach((motion) => motion.setValue(0));
      shakeCard();
    } finally {
      setBusy(false);
    }
  };
  const key = (value: string) => {
    if (pin.length >= 4) return;
    const next = `${pin}${value}`.slice(0, 4);
    setError("");
    setPin(next);
    animateSlot(next.length - 1);
    void submit(next);
  };
  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined" || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || busy) return;
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        key(event.key);
      } else if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        setPin((current) => current.slice(0, -1));
      } else if (event.key === "Enter") {
        event.preventDefault();
        void submit(pin);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pin, busy]);
  const orbitTargets = [
    { x: 78, y: -9, r: "0deg" },
    { x: 68, y: 33, r: "0deg" },
    { x: -26, y: 75, r: "0deg" },
    { x: -120, y: 33, r: "0deg" },
  ];
  const orbitRotation = orbitMotion.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });
  const collapseScale = collapseMotion.interpolate({
    inputRange: [0, 0.82, 1],
    outputRange: [1, 1, 0.86],
  });
  const collapseOpacity = collapseMotion.interpolate({
    inputRange: [0, 0.84, 1],
    outputRange: [1, 1, 0],
  });
  const successScale = successMotion.interpolate({
    inputRange: [0, 0.62, 1],
    outputRange: [0.35, 1.12, 1],
  });
  const ringScale = successMotion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.45, 1.75],
  });
  const ringOpacity = successMotion.interpolate({
    inputRange: [0, 0.55, 1],
    outputRange: [0, 0.5, 0],
  });
  const animating = verifyPhase !== "entry";
  return (
    <View style={s.pinPage}>
      <View style={s.pinGlow} />
      <Text style={s.overline}>THOMAS BLONDE</Text>
      <Text style={s.brand}>BLONDE</Text>
      <Animated.View style={[s.pinCard, { transform: [{ translateX: cardMotion }] }]}>
        {verified ? (
          <View style={s.verifiedWrap}>
            <View style={s.verifiedStage}>
              <Animated.View style={[s.verifiedRing, { opacity: ringOpacity, transform: [{ scale: ringScale }] }]} />
              <Animated.View style={[s.verifiedIcon, { transform: [{ scale: successScale }] }]}>
                <View style={s.verifiedGlassHighlight} />
                <Ionicons name="checkmark" size={27} color={C.gold} />
              </Animated.View>
            </View>
            <Text style={s.pinTitle}>Welcome to Blonde</Text>
            <Text style={s.note}>Your account has been verified.</Text>
            <Text style={s.verifiedLabel}>SECURE ACCESS GRANTED</Text>
          </View>
        ) : (
          <>
            <View style={s.lockBadge}>
              <Ionicons name="lock-closed-outline" size={22} color={C.gold} />
            </View>
            <Text style={s.pinTitle}>Enter your PIN</Text>
            <Text style={s.note}>Use the four-digit PIN assigned to your account.</Text>
            <Animated.View
              style={[
                s.pinSlotsStage,
                {
                  opacity: collapseOpacity,
                  transform: [
                    { scale: collapseScale },
                    {
                      rotate: verifyPhase === "verifying" ? orbitRotation : "0deg",
                    },
                  ],
                },
              ]}
            >
              <View style={s.pinSlots}>
                {[0, 1, 2, 3].map((i) => {
                  const target = orbitTargets[i];
                  const delay = i * 0.06;
                  const collapseX = collapseMotion.interpolate({
                    inputRange: [delay, 0.72 + delay],
                    outputRange: [target.x, 0],
                    extrapolate: "clamp",
                  });
                  const collapseY = collapseMotion.interpolate({
                    inputRange: [delay, 0.72 + delay],
                    outputRange: [target.y, 28],
                    extrapolate: "clamp",
                  });
                  const collapseRotate = collapseMotion.interpolate({
                    inputRange: [delay, 0.72 + delay],
                    outputRange: [target.r, "0deg"],
                    extrapolate: "clamp",
                  });
                  return (
                    <Animated.View
                      key={i}
                      style={[
                        s.pinSlot,
                        i < pin.length && s.pinSlotFilled,
                        {
                          transform: [
                            {
                              translateX:
                                verifyPhase === "collapsing"
                                  ? collapseX
                                  : animating
                                    ? formationMotion.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, target.x],
                                      })
                                    : 0,
                            },
                            {
                              translateY:
                                verifyPhase === "collapsing"
                                  ? collapseY
                                  : animating
                                    ? formationMotion.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, target.y],
                                      })
                                    : slotMotion[i].interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [18, 0],
                                      }),
                            },
                            {
                              rotate:
                                verifyPhase === "collapsing"
                                  ? collapseRotate
                                  : animating
                                    ? formationMotion.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: ["0deg", target.r],
                                      })
                                    : "0deg",
                            },
                            {
                              scale:
                                i < pin.length
                                  ? slotMotion[i].interpolate({
                                      inputRange: [0, 1],
                                      outputRange: [0.72, 1],
                                    })
                                  : 1,
                            },
                          ],
                          opacity: i < pin.length ? slotMotion[i] : 1,
                        },
                      ]}
                    >
                      <View style={s.pinSlotShine} />
                      <Text style={s.pinSlotText}>{i < pin.length ? "•" : ""}</Text>
                    </Animated.View>
                  );
                })}
              </View>
            </Animated.View>
            {busy || verifyPhase === "collapsing" ? (
              <View style={s.verifyingRow}>
                <Ionicons name="sparkles-outline" size={14} color={C.gold} />
                <Text style={s.verifyingText}>VERIFYING</Text>
              </View>
            ) : error ? (
              <Text style={s.error}>{error}</Text>
            ) : (
              <Text style={s.pinHint}>Your PIN stays private and secure.</Text>
            )}
            <View style={s.keypad}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((value, index) => (
                <Pressable
                  key={`${value}-${index}`}
                  disabled={!value || busy || verifyPhase === "collapsing"}
                  accessibilityRole="button"
                  accessibilityLabel={value === "⌫" ? "Delete digit" : value || "Spacer"}
                  onPress={() =>
                    value === "⌫"
                      ? setPin((current) => {
                          slotMotion[Math.max(0, current.length - 1)]?.setValue(0);
                          return current.slice(0, -1);
                        })
                      : key(value)
                  }
                  style={({ pressed }) => [s.key, !value && s.keyBlank, pressed && value ? s.keyPressed : null]}
                >
                  <Text style={s.keyText}>{value}</Text>
                </Pressable>
              ))}
            </View>
          </>
        )}
      </Animated.View>
    </View>
  );
}

type Recent = {
  agentId: string;
  question: string;
  answer: string;
  createdAt?: string;
};

function SwipeQnaRow({ item, onOpen, onDelete }: { item: Recent; onOpen: () => void; onDelete: () => void }) {
  const x = useRef(new Animated.Value(0)).current;
  const moved = useRef(false);
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_event, gesture) => Math.abs(gesture.dx) > 7 && Math.abs(gesture.dx) > Math.abs(gesture.dy),
      onPanResponderGrant: () => {
        moved.current = false;
      },
      onPanResponderMove: (_event, gesture) => {
        moved.current = Math.abs(gesture.dx) > 8;
        x.setValue(Math.max(-88, Math.min(0, gesture.dx)));
      },
      onPanResponderRelease: (_event, gesture) =>
        Animated.spring(x, {
          toValue: gesture.dx < -44 ? -88 : 0,
          useNativeDriver: true,
        }).start(),
      onPanResponderTerminate: () => Animated.spring(x, { toValue: 0, useNativeDriver: true }).start(),
    }),
  ).current;
  return (
    <View style={s.swipeRow}>
      <Pressable accessibilityRole="button" accessibilityLabel={`Delete ${item.question}`} onPress={onDelete} style={s.swipeDelete}>
        <Text style={s.swipeDeleteText}>Delete</Text>
      </Pressable>
      <Animated.View {...pan.panHandlers} style={{ transform: [{ translateX: x }] }}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Open question: ${item.question}`}
          onPress={() => {
            if (!moved.current) onOpen();
          }}
          style={s.card}
        >
          <Text numberOfLines={1} style={s.rowTitle}>
            {item.question}
          </Text>
          <Text style={s.agentLabel}>{agentName(item.agentId)}</Text>
        </Pressable>
      </Animated.View>
    </View>
  );
}

export function QnaHistoryModal({ visible, session, onClose, onDeleted }: { visible: boolean; session: Session | null; onClose: () => void; onDeleted?: (agentId: string, question: string, createdAt?: string) => void }) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [selected, setSelected] = useState<Recent | null>(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!session) return;
    setLoading(true);
    try {
      setRows((await listQna(session)).history ?? []);
    } catch (e) {
      Alert.alert("Could not load Q&A history", e instanceof Error ? e.message : "Try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (visible) void load();
  }, [visible, session?.access_token]);
  const recents = useMemo(
    () =>
      rows
        .flatMap((row) =>
          row.messages.flatMap((message, index, messages) =>
            message.role === "user"
              ? [
                  {
                    agentId: row.agent_id,
                    question: message.content,
                    answer: messages.slice(index + 1).find((item) => item.role === "assistant")?.content ?? "No answer was saved.",
                    createdAt: message.created_at,
                  },
                ]
              : [],
          ),
        )
        .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))),
    [rows],
  );
  const remove = async (item: Recent) => {
    if (!session) return;
    await deleteQna(session, item.agentId, item.question, item.createdAt);
    onDeleted?.(item.agentId, item.question, item.createdAt);
    setSelected(null);
    await load();
  };
  const copy = async (text: string) => {
    if (Platform.OS === "web" && navigator.clipboard) await navigator.clipboard.writeText(text);
    else Alert.alert("Copy unavailable", "Copy is available in the web app.");
  };
  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={s.page}>
        <Header title="Q&A History" close={onClose} />
        <ScrollView contentContainerStyle={s.body}>
          <Text style={s.section}>RECENTS</Text>
          {loading ? <Text style={s.empty}>Loading…</Text> : !recents.length ? <Text style={s.empty}>Questions and answers will appear here after this account chats with an agent.</Text> : recents.map((item, index) => <SwipeQnaRow key={`${item.agentId}-${item.createdAt ?? index}`} item={item} onOpen={() => setSelected(item)} onDelete={() => void remove(item)} />)}
        </ScrollView>
        {selected ? (
          <View style={s.detail}>
            <Header title={agentName(selected.agentId)} close={() => setSelected(null)} />
            <ScrollView contentContainerStyle={s.body}>
              <Text style={s.section}>QUESTION</Text>
              <View style={s.userBubble}>
                <Text style={s.userText}>{selected.question}</Text>
              </View>
              <Pressable onPress={() => copy(selected.question)} style={s.copy}>
                <Text>Copy</Text>
              </Pressable>
              <Text style={s.section}>ANSWER</Text>
              <View style={s.answerBubble}>
                <Text style={s.answerText}>{selected.answer}</Text>
              </View>
              <Pressable onPress={() => copy(selected.answer)} style={s.copy}>
                <Text>Copy</Text>
              </Pressable>
              <Pressable onPress={() => remove(selected)} style={s.delete}>
                <Text style={s.deleteText}>Delete this Q&A</Text>
              </Pressable>
            </ScrollView>
          </View>
        ) : null}
      </View>
    </Modal>
  );
}

export function ProfileEditorModal({ visible, session, person, onClose, onSaved }: { visible: boolean; session: Session | null; person: any; onClose: () => void; onSaved: (profile: { name: string; avatar_url: string }) => void }) {
  const [name, setName] = useState(person?.name ?? "");
  const [avatar, setAvatar] = useState(person?.avatar_url ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (visible) {
      setName(person?.name ?? "");
      setAvatar(person?.avatar_url ?? "");
    }
  }, [visible, person?.name, person?.avatar_url]);
  const choose = () => {
    if (Platform.OS !== "web") return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        Alert.alert("Photo too large", "Choose an image smaller than 2 MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setAvatar(String(reader.result ?? ""));
      reader.readAsDataURL(file);
    };
    input.click();
  };
  const save = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const saved = { name: name.trim(), avatar_url: avatar };
      await updateProfile(session, saved.name, saved.avatar_url);
      onSaved(saved);
      onClose();
    } catch (e) {
      Alert.alert("Could not update profile", e instanceof Error ? e.message : "Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.dialog}>
          <Header title="Edit profile" close={onClose} />
          {avatar ? (
            <Image
              source={{ uri: avatar }}
              style={{
                width: 76,
                height: 76,
                borderRadius: 38,
                alignSelf: "center",
                marginVertical: 14,
              }}
            />
          ) : (
            <View style={s.photoFallback}>
              <Text style={s.photoInitial}>{(name || "Y").charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <Pressable style={s.secondary} onPress={choose}>
            <Text>Choose photo</Text>
          </Pressable>
          <TextInput accessibilityLabel="Profile name" value={name} onChangeText={setName} style={s.input} placeholder="Your name" />
          <Pressable disabled={busy || !name.trim()} style={s.primary} onPress={save}>
            <Text style={s.primaryText}>{busy ? "Saving…" : "Save profile"}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

export function ProductCenterModal({ visible, session, person, initialTab = "setup", onClose, onReport, onNotificationsSeen }: { visible: boolean; session: Session | null; person: any; initialTab?: string; onClose: () => void; onReport?: (reportId: string) => void; onNotificationsSeen?: () => void }) {
  const [tab, setTab] = useState(initialTab);
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [savedQuery, setSavedQuery] = useState("");
  const [savedAgent, setSavedAgent] = useState("all");
  const [prefs, setPrefs] = useState({
    enabled: true,
    last_seen_at: null as string | null,
  });
  const loadRequest = useRef(0);
  const loadTab = async (next: string) => {
    if (!session) return null;
    const value: any = next === "notifications" ? await loadBusinessChanges(session) : next === "saved" ? await loadSavedReports(session) : next === "health" ? await loadSystemHealth(session) : await listQna(session);
    if (value.prefs?.notifications)
      setPrefs({
        enabled: value.prefs.notifications.enabled !== false,
        last_seen_at: value.prefs.notifications.last_seen_at ?? null,
      });
    if (next === "notifications") {
      onNotificationsSeen?.();
      void markBusinessChangesSeen(session).catch(() => undefined);
    }
    return value;
  };
  useEffect(() => {
    if (!visible || !session) {
      loadRequest.current += 1;
      return;
    }
    const request = ++loadRequest.current;
    setTab(initialTab);
    setLoading(true);
    loadTab(initialTab)
      .then((value) => {
        if (request === loadRequest.current) setContent(value);
      })
      .catch((e) => {
        if (request === loadRequest.current) setContent({ error: e.message });
      })
      .finally(() => {
        if (request === loadRequest.current) setLoading(false);
      });
  }, [visible, initialTab, session?.access_token]);
  const select = async (next: string) => {
    if (!session) return;
    const request = ++loadRequest.current;
    setTab(next);
    void recordUsage(session, "control-center-tab", { tab: next }).catch(() => undefined);
    setLoading(true);
    try {
      const value = await loadTab(next);
      if (request === loadRequest.current) setContent(value);
    } catch (e) {
      if (request === loadRequest.current)
        setContent({
          error: e instanceof Error ? e.message : "Could not load.",
        });
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  };
  const age = (iso?: string) => {
    if (!iso) return "No timestamp";
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`;
  };
  const savedReports = (content?.reports ?? []).filter((r: any) => (savedAgent === "all" || r.agent_id === savedAgent) && (!savedQuery.trim() || `${r.headline} ${agentName(r.agent_id)}`.toLowerCase().includes(savedQuery.trim().toLowerCase())));
  const laneStatus = (lane: any) => {
    const source = lane.source_at ? new Date(lane.source_at).getTime() : 0;
    const hours = source ? (Date.now() - source) / 3600000 : Infinity;
    const fast = ["shopify-retail", "shopify-wholesale", "owner"].includes(lane.agent_id);
    const staleAfter = fast ? 1 : 6;
    return lane.last_error ? { label: "FAILED", bad: true } : hours > staleAfter ? { label: "STALE", bad: true } : { label: "LIVE", bad: false };
  };
  const saveNotifications = async () => {
    if (!session) return;
    try {
      if (prefs.enabled) {
        const result = await enablePushNotifications(session);
        if (result !== "granted") throw new Error(result === "unsupported" ? "This device does not support push notifications." : "Notification permission was not granted.");
      }
      await saveNotificationPreferences(session, {
        enabled: prefs.enabled,
        last_seen_at: prefs.last_seen_at,
      });
      Alert.alert("Saved", prefs.enabled ? "Blonde will alert you only when verified business data changes enough to matter." : "Business-change notifications are off for this account.");
    } catch (e) {
      Alert.alert("Could not save", e instanceof Error ? e.message : "Try again.");
    }
  };
  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={s.page}>
        <Header title="Blonde Control Center" close={onClose} />
        <ScrollView horizontal style={s.tabs} contentContainerStyle={s.tabRow}>
          {[
            ["setup", "Get set up"],
            ["notifications", "Notifications"],
            ["saved", "Saved"],
            ["health", "System Health"],
          ].map(([id, label]) => (
            <Pressable key={id} onPress={() => select(id)} style={[s.tab, tab === id && s.tabOn]}>
              <Text style={tab === id ? s.tabTextOn : s.tabText}>{label}</Text>
            </Pressable>
          ))}
        </ScrollView>
        <ScrollView contentContainerStyle={s.body}>
          {loading ? (
            <Text style={s.empty}>Loading…</Text>
          ) : content?.error ? (
            <Text style={s.error}>{content.error}</Text>
          ) : tab === "setup" ? (
            <>
              {[
                ["Profile photo", Boolean(person?.avatar_url), "Add a photo so this account is easy to recognize."],
                ["Notifications", Platform.OS === "web" && Notification.permission === "granted", "Blonde stays quiet unless verified business data changes enough to matter."],
                ["First agent question", Boolean(content?.history?.length), "Ask any agent a question."],
                ["Installed app", Platform.OS === "web" && matchMedia("(display-mode: standalone)").matches, "Install Blonde for the best experience."],
              ].map(([name, ok, note]) => (
                <View key={String(name)} style={s.statusRow}>
                  <View style={s.flex}>
                    <Text style={s.rowTitle}>{name}</Text>
                    <Text style={s.note}>{note}</Text>
                  </View>
                  <Text style={[s.badge, !ok && s.badgeBad]}>{ok ? "DONE" : "TO DO"}</Text>
                </View>
              ))}
            </>
          ) : tab === "notifications" ? (
            <>
              <View style={s.notificationIntro}>
                <Ionicons name="pulse-outline" size={22} color={C.gold} />
                <View style={s.flex}>
                  <Text style={s.rowTitle}>Meaningful business changes only</Text>
                  <Text style={s.notificationCopy}>Routine reports and successful syncs stay quiet. Blonde alerts you when verified data crosses an action threshold, changes to a different issue, or returns to normal.</Text>
                </View>
              </View>
              <ToggleRow label="Receive meaningful alerts" value={prefs.enabled} onValue={(enabled) => setPrefs({ ...prefs, enabled })} />
              <Pressable style={s.primary} onPress={saveNotifications}>
                <Text style={s.primaryText}>Save notification setting</Text>
              </Pressable>
              <Text style={s.section}>RECENT CHANGES</Text>
              {!(content?.changes ?? []).length ? (
                <Text style={s.empty}>No meaningful business changes right now. Routine activity is being filtered out.</Text>
              ) : (
                (content.changes as BusinessChange[]).map((change) => (
                  <Pressable accessibilityRole="button" accessibilityLabel={`Open ${change.headline}`} key={change.id} style={s.changeCard} onPress={() => onReport?.(change.report_id)}>
                    <View style={s.changeTop}>
                      <Text style={[s.changeLabel, change.kind === "improvement" && s.changeLabelGood]}>{change.label}</Text>
                      <Text style={s.changeMeta}>
                        {agentName(change.agent_id)} · {age(change.created_at)}
                      </Text>
                    </View>
                    <Text style={s.rowTitle}>{change.headline}</Text>
                    <Text style={s.changeSummary}>{change.summary}</Text>
                    {change.action ? <Text style={s.changeAction}>{change.action}</Text> : null}
                  </Pressable>
                ))
              )}
            </>
          ) : tab === "saved" ? (
            <>
              <TextInput accessibilityLabel="Search saved reports" value={savedQuery} onChangeText={setSavedQuery} placeholder="Search saved reports" style={s.input} />
              <ScrollView horizontal contentContainerStyle={s.filterRow}>
                {[["all", "All"], ...backendAgents.map((a) => [a.backendId, a.name])].map(([id, label]) => (
                  <Pressable key={id} onPress={() => setSavedAgent(id)} style={[s.miniFilter, savedAgent === id && s.miniFilterOn]}>
                    <Text style={savedAgent === id ? s.tabTextOn : s.tabText}>{label}</Text>
                  </Pressable>
                ))}
              </ScrollView>
              {!(content?.reports ?? []).length ? (
                <Text style={s.empty}>No saved reports yet. Open a report and tap the bookmark to keep it here.</Text>
              ) : !savedReports.length ? (
                <Text style={s.empty}>No saved reports match this search.</Text>
              ) : (
                savedReports.map((r: any) => (
                  <View key={r.id} style={s.card}>
                    <Text style={s.rowTitle}>{r.headline}</Text>
                    <Text style={s.note}>
                      {agentName(r.agent_id)} · Published {age(r.created_at)}
                    </Text>
                  </View>
                ))
              )}
            </>
          ) : (
            (content?.lanes ?? []).map((lane: any) => {
              const status = laneStatus(lane);
              return (
                <View key={lane.agent_id} style={s.statusRow}>
                  <View style={s.flex}>
                    <Text style={s.rowTitle}>{agentName(lane.agent_id)}</Text>
                    <Text style={s.note}>
                      Source last changed {age(lane.source_at)}
                      {`\n`}Report published {age(lane.created_at)}
                      {lane.next_run_at ? `\nNext scheduled check ${new Date(lane.next_run_at).toLocaleString()}` : ""}
                      {lane.last_error ? `\nFailure: ${lane.last_error}` : ""}
                      {lane.source_note ? `\n${lane.source_note}` : ""}
                    </Text>
                  </View>
                  <Text style={[s.badge, status.bad && s.badgeBad]}>{status.label}</Text>
                </View>
              );
            })
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}

function ToggleRow({ label, value, onValue }: { label: string; value: boolean; onValue: (value: boolean) => void }) {
  return (
    <View style={s.statusRow}>
      <Text style={[s.rowTitle, s.flex]}>{label}</Text>
      <Switch value={value} onValueChange={onValue} trackColor={{ false: "#D8D0C6", true: C.goldSoft }} thumbColor={value ? C.gold : C.white} />
    </View>
  );
}

const s = StyleSheet.create({
  page: { flex: 1, backgroundColor: C.paper },
  header: {
    minHeight: 64,
    paddingHorizontal: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: C.line,
  },
  title: { fontSize: 20, fontWeight: "800", color: C.ink },
  close: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: C.card,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: C.line,
  },
  body: { padding: 18, paddingBottom: 50 },
  section: {
    fontSize: 10,
    fontWeight: "900",
    letterSpacing: 1.2,
    color: C.gold,
    marginTop: 16,
    marginBottom: 8,
  },
  card: {
    backgroundColor: C.card,
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 14,
    padding: 14,
  },
  notificationIntro: {
    backgroundColor: C.goldSoft,
    borderWidth: 1,
    borderColor: "#DFC59F",
    borderRadius: 16,
    padding: 14,
    marginBottom: 10,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 11,
  },
  notificationCopy: { fontSize: 12, lineHeight: 18, color: C.muted, marginTop: 4 },
  changeCard: {
    backgroundColor: C.card,
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 16,
    padding: 14,
    marginBottom: 9,
  },
  changeTop: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 },
  changeLabel: {
    fontSize: 9,
    fontWeight: "900",
    letterSpacing: 1,
    color: C.bad,
    backgroundColor: "#F3DCD7",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  changeLabelGood: { color: "#536B50", backgroundColor: "#E2EBDD" },
  changeMeta: { fontSize: 10, color: C.muted, textTransform: "capitalize" },
  changeSummary: { fontSize: 13, lineHeight: 19, color: C.muted, marginTop: 6 },
  changeAction: { fontSize: 12, lineHeight: 18, color: C.ink, fontWeight: "700", marginTop: 8 },
  swipeRow: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 14,
    backgroundColor: C.card,
    marginBottom: 8,
  },
  swipeDelete: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 88,
    backgroundColor: C.bad,
    alignItems: "center",
    justifyContent: "center",
  },
  swipeDeleteText: { color: C.white, fontWeight: "800" },
  rowTitle: { fontSize: 15, fontWeight: "700", color: C.ink },
  agentLabel: {
    fontSize: 11,
    fontWeight: "800",
    color: C.gold,
    textTransform: "uppercase",
    marginTop: 5,
  },
  note: {
    fontSize: 12,
    lineHeight: 17,
    color: C.muted,
    marginTop: 4,
    textAlign: "center",
  },
  empty: { textAlign: "center", color: C.muted, lineHeight: 20, padding: 28 },
  detail: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: C.paper,
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "84%",
    backgroundColor: C.ink,
    borderRadius: 16,
    padding: 14,
  },
  userText: { color: C.white, lineHeight: 20 },
  answerBubble: {
    backgroundColor: C.card,
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 16,
    padding: 14,
  },
  answerText: { color: C.ink, lineHeight: 20 },
  copy: {
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: C.line,
    marginTop: 7,
  },
  delete: {
    borderWidth: 1,
    borderColor: "#EBCBC7",
    borderRadius: 14,
    padding: 13,
    marginTop: 28,
    alignItems: "center",
  },
  deleteText: { color: C.bad, fontWeight: "800" },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(33,29,25,.4)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  dialog: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 22,
    backgroundColor: C.card,
    padding: 18,
  },
  photoFallback: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: C.gold,
    alignSelf: "center",
    marginVertical: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  photoInitial: { color: C.white, fontSize: 26, fontWeight: "800" },
  secondary: { alignSelf: "center", padding: 10 },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 12,
    paddingHorizontal: 13,
    color: C.ink,
    marginTop: 10,
  },
  filterRow: { gap: 7, paddingVertical: 12 },
  miniFilter: {
    borderWidth: 1,
    borderColor: C.line,
    backgroundColor: C.card,
    borderRadius: 999,
    paddingHorizontal: 11,
    paddingVertical: 7,
  },
  miniFilterOn: { backgroundColor: C.ink },
  primary: {
    minHeight: 48,
    borderRadius: 13,
    backgroundColor: C.gold,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 14,
    paddingHorizontal: 14,
  },
  primaryText: { color: C.white, fontWeight: "800" },
  error: {
    color: C.bad,
    textAlign: "center",
    marginVertical: 10,
    minHeight: 16,
  },
  tabs: { flexGrow: 0 },
  tabRow: { padding: 12, gap: 7 },
  tab: {
    borderWidth: 1,
    borderColor: C.line,
    backgroundColor: C.card,
    borderRadius: 999,
    paddingHorizontal: 13,
    paddingVertical: 9,
  },
  tabOn: { backgroundColor: C.ink },
  tabText: { color: C.ink, fontWeight: "700" },
  tabTextOn: { color: C.white, fontWeight: "700" },
  statusRow: {
    minHeight: 70,
    backgroundColor: C.card,
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 14,
    padding: 13,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  flex: { flex: 1 },
  badge: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: C.goldSoft,
    color: "#76542E",
    fontSize: 10,
    fontWeight: "900",
  },
  badgeBad: { backgroundColor: "#F3DCD7", color: "#9B392F" },
  pinPage: {
    flex: 1,
    backgroundColor: C.paper,
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    overflow: "hidden",
  },
  pinGlow: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: 210,
    backgroundColor: "#F1E3CF",
    opacity: 0.72,
    top: -180,
    right: -160,
  },
  overline: {
    color: C.muted,
    fontSize: 8,
    letterSpacing: 2.2,
    fontWeight: "700",
  },
  brand: {
    color: C.gold,
    fontSize: 26,
    letterSpacing: 5.5,
    marginTop: 4,
    marginBottom: 14,
  },
  pinCard: {
    width: "100%",
    maxWidth: 344,
    backgroundColor: "rgba(255,252,247,.94)",
    borderWidth: 1,
    borderColor: "#D9C7AE",
    borderRadius: 26,
    paddingHorizontal: 20,
    paddingVertical: 20,
    alignItems: "center",
    shadowColor: "#6B5134",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 7,
  },
  lockBadge: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: C.goldSoft,
    borderWidth: 1,
    borderColor: "#DFC59F",
    alignItems: "center",
    justifyContent: "center",
  },
  pinTitle: { fontSize: 20, fontWeight: "800", color: C.ink, marginTop: 10 },
  pinSlotsStage: {
    width: 200,
    height: 126,
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 5,
  },
  pinSlots: { flexDirection: "row", gap: 8, marginTop: 18, marginBottom: 8 },
  pinSlot: {
    width: 44,
    height: 48,
    borderRadius: 14,
    backgroundColor: "rgba(255,252,247,.72)",
    borderWidth: 1,
    borderColor: C.line,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  pinSlotFilled: {
    backgroundColor: "rgba(255,248,237,.78)",
    borderColor: C.gold,
    shadowColor: C.gold,
    shadowOpacity: 0.16,
    shadowRadius: 7,
  },
  pinSlotShine: {
    position: "absolute",
    top: 3,
    left: 6,
    width: 19,
    height: 6,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,.72)",
    transform: [{ rotate: "-12deg" }],
  },
  pinSlotText: {
    fontSize: 23,
    lineHeight: 26,
    color: C.gold,
    fontWeight: "900",
  },
  pinHint: {
    color: C.muted,
    fontSize: 9.5,
    marginTop: -2,
    marginBottom: 10,
    minHeight: 16,
  },
  verifyingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 0,
    marginBottom: 10,
    minHeight: 16,
  },
  verifyingText: {
    color: C.gold,
    fontSize: 9,
    letterSpacing: 1.3,
    fontWeight: "900",
  },
  keypad: { flexDirection: "row", flexWrap: "wrap", width: 240, gap: 12 },
  key: {
    width: 72,
    height: 56,
    borderRadius: 19,
    backgroundColor: "rgba(247,243,236,.9)",
    borderWidth: 1,
    borderColor: "#E8DED0",
    alignItems: "center",
    justifyContent: "center",
  },
  keyPressed: {
    backgroundColor: C.goldSoft,
    borderColor: C.gold,
    transform: [{ scale: 0.95 }],
  },
  keyBlank: { backgroundColor: "transparent", borderColor: "transparent" },
  keyText: { fontSize: 20, color: C.ink, fontWeight: "600" },
  verifiedWrap: {
    minHeight: 401,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  verifiedStage: {
    width: 106,
    height: 106,
    alignItems: "center",
    justifyContent: "center",
  },
  verifiedRing: {
    position: "absolute",
    width: 66,
    height: 66,
    borderRadius: 33,
    borderWidth: 1.5,
    borderColor: "rgba(184,139,83,.72)",
    backgroundColor: "rgba(239,224,201,.12)",
  },
  verifiedIcon: {
    width: 54,
    height: 54,
    borderRadius: 17,
    backgroundColor: "rgba(255,252,247,.76)",
    borderWidth: 1,
    borderColor: "rgba(184,139,83,.58)",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: C.gold,
    shadowOpacity: 0.18,
    shadowRadius: 12,
    overflow: "hidden",
  },
  verifiedGlassHighlight: {
    position: "absolute",
    top: 5,
    left: 8,
    width: 23,
    height: 7,
    borderRadius: 7,
    backgroundColor: "rgba(255,255,255,.8)",
    transform: [{ rotate: "-14deg" }],
  },
  verifiedLabel: {
    marginTop: 16,
    color: C.gold,
    fontSize: 9,
    fontWeight: "900",
    letterSpacing: 1.6,
  },
});
